import { lazy } from 'react';

// project imports
import Loadable from 'components/Loadable';
import DashboardLayout from 'layout/Dashboard';
import RequireAuth from 'features/auth/RequireAuth';
import RequirePermission from 'features/auth/RequirePermission';

// render- Dashboard
const DashboardDefault = Loadable(lazy(() => import('pages/dashboard/default')));

// render - color
const Color = Loadable(lazy(() => import('pages/component-overview/color')));
const Typography = Loadable(lazy(() => import('pages/component-overview/typography')));
const Shadow = Loadable(lazy(() => import('pages/component-overview/shadows')));

// render - sample page
const SamplePage = Loadable(lazy(() => import('pages/extra-pages/sample-page')));

// render - errors
const Forbidden = Loadable(lazy(() => import('pages/errors/Forbidden')));

// render - usuarios & roles
const UsuariosListPage = Loadable(lazy(() => import('features/usuarios/pages/UsuariosListPage')));
const RolesListPage = Loadable(lazy(() => import('features/roles/pages/RolesListPage')));
const BloqueosPage = Loadable(lazy(() => import('features/bloqueos/pages/BloqueosPage')));

// render - cobro de la plataforma (PROP-002)
const CobroPlataformaPage = Loadable(lazy(() => import('features/plataforma/pages/CobroPlataformaPage')));

// render - registro de actividad / logs (US-009)
const RegistroActividadPage = Loadable(lazy(() => import('features/registroActividad/pages/RegistroActividadPage')));

// render - gestión de trabajadores (US-006 crear, US-007 dispositivos, US-008 listado)
const GestionTrabajadoresPage = Loadable(lazy(() => import('features/trabajadores/pages/GestionTrabajadoresPage')));
const TrabajadoresListPage = Loadable(lazy(() => import('features/trabajadores/pages/TrabajadoresListPage')));
const TrabajadorFormPage = Loadable(lazy(() => import('features/trabajadores/pages/TrabajadorFormPage')));
const DispositivosPage = Loadable(lazy(() => import('features/trabajadores/pages/DispositivosPage')));

// render - clientes (US-012 nuevo, US-013 lista)
const ClientesModulePage = Loadable(lazy(() => import('features/clientes/pages/ClientesModulePage')));
const ClienteFormPage = Loadable(lazy(() => import('features/clientes/pages/ClienteFormPage')));
const ClientesListPage = Loadable(lazy(() => import('features/clientes/pages/ClientesListPage')));

// render - tipos de movimiento (US-018 crear, US-019 histórico)
const TiposMovimientoModulePage = Loadable(lazy(() => import('features/tiposMovimiento/pages/TiposMovimientoModulePage')));
const CrearTipoMovimientoPage = Loadable(lazy(() => import('features/tiposMovimiento/pages/CrearTipoMovimientoPage')));
const HistoricoTiposMovimientoPage = Loadable(lazy(() => import('features/tiposMovimiento/pages/HistoricoTiposMovimientoPage')));

// render - rutas
const RutasListPage = Loadable(lazy(() => import('features/rutas/pages/RutasListPage')));
const RutaCreatePage = Loadable(lazy(() => import('features/rutas/pages/RutaCreatePage')));
const RutaDetailPage = Loadable(lazy(() => import('features/rutas/pages/RutaDetailPage')));

// render - prestamos
const PrestamosListPage = Loadable(lazy(() => import('features/prestamos/pages/PrestamosListPage')));
const PrestamoDetailPage = Loadable(lazy(() => import('features/prestamos/pages/PrestamoDetailPage')));

// render - recaudo
const RecaudoListPage = Loadable(lazy(() => import('features/recaudo/pages/RecaudoListPage')));
const CajasListPage = Loadable(lazy(() => import('features/cajas/pages/CajasListPage')));
const PanelPrincipalPage = Loadable(lazy(() => import('features/panelPrincipal/pages/PanelPrincipalPage')));
const SubirCarteraPage = Loadable(lazy(() => import('features/cartera/pages/SubirCarteraPage')));

// ==============================|| MAIN ROUTING ||============================== //

const MainRoutes = {
  path: '/',
  element: <RequireAuth />,
  children: [
    {
      element: <DashboardLayout />,
      children: [
        {
          path: '/',
          element: <DashboardDefault />
        },
        {
          path: 'dashboard',
          children: [
            {
              path: 'default',
              element: <DashboardDefault />
            }
          ]
        },
        {
          path: 'typography',
          element: <Typography />
        },
        {
          path: 'color',
          element: <Color />
        },
        {
          path: 'shadow',
          element: <Shadow />
        },
        {
          path: 'sample-page',
          element: <SamplePage />
        },
        {
          path: '403',
          element: <Forbidden />
        },
        {
          path: 'usuarios',
          element: (
            <RequirePermission code="usuarios.read">
              <UsuariosListPage />
            </RequirePermission>
          )
        },
        {
          path: 'roles',
          element: (
            <RequirePermission code="roles.read">
              <RolesListPage />
            </RequirePermission>
          )
        },
        {
          // Bloqueo de la app móvil (US-001).
          path: 'bloqueos',
          element: (
            <RequirePermission code="bloqueos.read">
              <BloqueosPage />
            </RequirePermission>
          )
        },
        {
          // Cobro del uso de la plataforma a las unidades (PROP-002). Va bajo
          // Administración y no bajo Recaudo: aquel es el cobro operativo de
          // cartera a los clientes de la ruta, que es otro flujo.
          //
          // `plataforma.read` todavía no existe en el catálogo de permisos del
          // backend, porque el módulo está pendiente de definición. Hasta que se
          // cree, nadie lo tiene y la pantalla queda inaccesible: es el
          // comportamiento correcto para un módulo sin reglas, y el código
          // definitivo debe confirmarse con el backend al escribir la US.
          path: 'plataforma/cobro',
          element: (
            <RequirePermission code="plataforma.read">
              <CobroPlataformaPage />
            </RequirePermission>
          )
        },
        {
          // Registro de actividad / auditoría (US-009). Solo lectura: no hay
          // rutas de escritura, la traza la generan los propios módulos de
          // negocio en el backend.
          path: 'registro-actividad',
          element: (
            <RequirePermission code="logs.read">
              <RegistroActividadPage />
            </RequirePermission>
          )
        },
        {
          // Gestión de trabajadores: las tres opciones son pestañas de una misma
          // página (US-006), así que es una ruta de layout con hijos.
          path: 'trabajadores',
          element: <GestionTrabajadoresPage />,
          children: [
            {
              path: 'listado',
              element: (
                <RequirePermission code="trabajadores.read">
                  <TrabajadoresListPage />
                </RequirePermission>
              )
            },
            {
              path: 'crear',
              element: (
                <RequirePermission code="trabajadores.create">
                  <TrabajadorFormPage />
                </RequirePermission>
              )
            },
            {
              // Editar reutiliza la vista de crear con los datos cargados
              // (US-008), por eso cuelga de la misma ruta.
              path: 'crear/:id',
              element: (
                <RequirePermission code={['trabajadores.create', 'trabajadores.update']}>
                  <TrabajadorFormPage />
                </RequirePermission>
              )
            },
            {
              path: 'dispositivos',
              element: (
                <RequirePermission code="dispositivos.manage">
                  <DispositivosPage />
                </RequirePermission>
              )
            }
          ]
        },
        {
          // Clientes: "Nuevo Cliente" y "Lista de Clientes" son pestañas de una
          // misma página (US-012/013), así que es una ruta de layout con hijos.
          path: 'clientes',
          element: <ClientesModulePage />,
          children: [
            {
              path: 'lista',
              element: (
                <RequirePermission code="clientes.read">
                  <ClientesListPage />
                </RequirePermission>
              )
            },
            {
              path: 'nuevo',
              element: (
                <RequirePermission code="clientes.create">
                  <ClienteFormPage />
                </RequirePermission>
              )
            },
            {
              // Editar reutiliza la vista de nuevo con los datos cargados (US-013).
              path: 'nuevo/:id',
              element: (
                <RequirePermission code="clientes.update">
                  <ClienteFormPage />
                </RequirePermission>
              )
            }
          ]
        },
        {
          // Tipos de movimiento: "Tipo de Movimiento" (crear, US-018) e "Histórico de
          // Movimientos" (consultar/administrar, US-019) son pestañas de una misma página.
          path: 'tipos-movimiento',
          element: <TiposMovimientoModulePage />,
          children: [
            {
              path: 'crear',
              element: (
                <RequirePermission code="tipos-movimiento.create">
                  <CrearTipoMovimientoPage />
                </RequirePermission>
              )
            },
            {
              path: 'historico',
              element: (
                <RequirePermission code="tipos-movimiento.read">
                  <HistoricoTiposMovimientoPage />
                </RequirePermission>
              )
            }
          ]
        },
        {
          path: 'rutas',
          element: (
            <RequirePermission code="rutas.read">
              <RutasListPage />
            </RequirePermission>
          )
        },
        {
          path: 'rutas/crear',
          element: (
            <RequirePermission code="rutas.create">
              <RutaCreatePage />
            </RequirePermission>
          )
        },
        {
          path: 'rutas/:id',
          element: (
            <RequirePermission code="rutas.read">
              <RutaDetailPage />
            </RequirePermission>
          )
        },
        {
          path: 'prestamos',
          element: (
            <RequirePermission code="prestamos.read">
              <PrestamosListPage />
            </RequirePermission>
          )
        },
        {
          path: 'prestamos/:id',
          element: (
            <RequirePermission code="prestamos.read">
              <PrestamoDetailPage />
            </RequirePermission>
          )
        },
        {
          path: 'recaudo',
          element: (
            <RequirePermission code="recaudo.read">
              <RecaudoListPage />
            </RequirePermission>
          )
        },
        {
          // Página principal "Desempeño de la ruta" (US-015). Mismo permiso que
          // /cajas: GET /panel-principal/cajas/:id/resumen exige caja.consultar.
          path: 'panel-principal',
          element: (
            <RequirePermission code="caja.consultar">
              <PanelPrincipalPage />
            </RequirePermission>
          )
        },
        {
          // GET /cajas exige caja.consultar en el backend; la ruta se protege
          // con el mismo código para no llevar al usuario a una pantalla que
          // solo podría mostrarle un 403.
          path: 'cajas',
          element: (
            <RequirePermission code="caja.consultar">
              <CajasListPage />
            </RequirePermission>
          )
        },
        {
          // "Subir cartera" (US-005). Por ahora sin permiso en el frontend: se ve
          // en el menú (Caja > Subir cartera) y elige la ruta en la pantalla. El
          // backend sigue exigiendo cartera.cargar y la pantalla muestra su 403.
          // TODO(US-010): proteger con RequirePermission code="cartera.cargar".
          path: 'cartera/subir',
          element: <SubirCarteraPage />
        },
        {
          // Misma pantalla, con la ruta ya elegida: se abre desde la caja web.
          path: 'cajas/ruta/:rutaId/subir-cartera',
          element: <SubirCarteraPage />
        }
      ]
    }
  ]
};

export default MainRoutes;
